<div class="container mx-auto space-y-6">
  <!-- Header Section -->
  <div class="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-base-300">
    <div>
      <h2 class="text-2xl font-bold text-base-content flex items-center gap-2.5">
        <i class="fa-solid fa-receipt text-primary text-xl"></i>
        <span>My Orders</span>
      </h2>
      <p class="text-xs sm:text-sm text-base-content/60 mt-1">
        View your course purchases, installment payments, and order verification statuses.
      </p>
    </div>
    <a routerLink="/courses" class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-primary gap-2">
      <i class="fa-solid fa-compass text-xs"></i>
      <span>Explore Courses</span>
    </a>
  </div>

  @if (isLoading) {
    <div class="flex justify-center items-center py-20">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"></span>
    </div>
  } @else if (orders.length > 0) {
    @if (hasAwaitingApprovalOrders) {
      <div class="daisy-alert daisy-alert-info text-xs sm:text-sm py-2.5 px-4 shadow-sm">
        <i class="fa-solid fa-circle-info text-info text-base shrink-0"></i>
        <span>
          Orders marked as <strong>Verification Pending</strong> have their payment slip submitted and are currently being reviewed by our administration team.
        </span>
      </div>
    }

    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="daisy-table daisy-table-zebra w-full">
          <thead>
            <tr class="bg-base-200/50 text-xs text-base-content/70">
              <th>Order ID</th>
              <th>Courses</th>
              <th>Date</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th class="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (order of orders; track (order.orderId || order._id)) {
              @let badge = getStatusBadge(order.status);
              <tr class="hover">
                <td class="font-mono text-xs font-semibold text-base-content">
                  #{{ order.orderId || order._id }}
                </td>
                <td>
                  <div class="space-y-1">
                    @for (item of order.items; track item) {
                      <div class="text-sm font-medium text-base-content flex items-center gap-1.5">
                        <i class="fa-solid fa-book-bookmark text-primary/70 text-xs"></i>
                        <span>{{ item.title || 'Course' }}</span>
                      </div>
                    }
                    @if (order.paymentPlan === 'INSTALLMENT') {
                      <span class="daisy-badge daisy-badge-ghost daisy-badge-xs text-base-content/70">
                        Installment Plan
                      </span>
                    }
                  </div>
                </td>
                <td class="text-xs text-base-content/70 whitespace-nowrap">
                  {{ formatDate(order.createdOn) }}
                </td>
                <td class="whitespace-nowrap">
                  <span class="font-semibold text-sm text-base-content">
                    {{ order.currency || 'LKR' }} {{ (order.totalAmount || 0) | number:'1.2-2' }}
                  </span>
                  @if (order.paidAmount && order.paidAmount > 0 && order.paidAmount < (order.totalAmount || 0)) {
                    <div class="text-[11px] text-base-content/50">
                      Paid: {{ order.currency || 'LKR' }} {{ order.paidAmount | number:'1.2-2' }}
                    </div>
                  }
                </td>
                <td>
                  <span class="daisy-badge daisy-badge-sm gap-1.5 {{ badge.badgeClass }} font-semibold shrink-0">
                    <i class="fa-solid {{ badge.icon }} text-[10px]"></i>
                    <span>{{ badge.label }}</span>
                  </span>
                </td>
                <td class="text-right whitespace-nowrap">
                  @if (order.status === OrderStatus.AWAITING_APPROVAL) {
                    <span
                      class="text-xs text-warning font-medium inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-warning/10"
                      title="Payment proof submitted. Admin approval in progress."
                    >
                      <i class="fa-solid fa-hourglass-half text-[10px]"></i> Under Review
                    </span>
                  } @else if (order.status === OrderStatus.PENDING || order.status === OrderStatus.OVERDUE) {
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-xs gap-1.5 {{ order.status === OrderStatus.OVERDUE ? 'daisy-btn-error text-white' : 'daisy-btn-warning' }}"
                      (click)="payOrder(order.orderId || order._id)"
                    >
                      <i class="fa-solid fa-credit-card text-[10px]"></i>
                      <span>Pay Now</span>
                    </button>
                  } @else if (order.status === OrderStatus.COMPLETED || order.status === OrderStatus.PARTIALLY_PAID) {
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1"
                      (click)="viewCourse(order)"
                    >
                      <i class="fa-solid fa-play text-[9px]"></i>
                      <span>Go to Course</span>
                    </button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  } @else {
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm p-12 text-center flex flex-col items-center justify-center space-y-4">
      <div class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center text-base-content/40">
        <i class="fa-solid fa-receipt text-3xl"></i>
      </div>
      <div class="space-y-1">
        <h3 class="text-lg font-bold text-base-content">No Orders Found</h3>
        <p class="text-sm text-base-content/60 max-w-sm">
          You haven't placed any course orders yet. Browse our catalog to find your next course!
        </p>
      </div>
      <a routerLink="/courses" class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2">
        <i class="fa-solid fa-compass text-xs"></i>
        <span>Explore Courses</span>
      </a>
    </div>
  }
</div>
